import {
  Box,
  Typography,
  Divider,
  InputBaseComponentProps,
  InputLabel,
  useMediaQuery,
  Stack,
  FormControlLabel,
} from '@mui/material'
import Calendar from 'components/Calendar/Calendar'
import { CheckboxLabel } from 'components/CheckboxLabel/CheckboxLabel'
import Input from 'components/Input/Input'
import { RequiredLabel } from 'components/RequiredLabel/RequiredLabel'
import { SelectDropdown } from 'components/SelectDropdown/SelectDropdown'
import TimePicker from 'components/TimePicker/TimePicker'
import { format } from 'date-fns'
import { Field, Form, FormikContextType, useFormikContext } from 'formik'
import theme from 'lib/theme'
import { formatDate } from 'lib/utils'
import { useServiceAreas } from 'providers/ServiceAreasProvider'
import { ChangeEventHandler, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { BlueSwitch } from 'components/Switch/Switch.styled'
import { DatePickerButton } from './ServiceArea.styled'

type CalendarSelectInputProps = {
  onChange: ChangeEventHandler<HTMLInputElement>
  disabled: boolean
  inputProps?: InputBaseComponentProps
  date: string
  open: boolean
  setOpen: (value: boolean) => void
  value?: Date
  id: string
  label: string | ReactNode
  width?: string
}

type TimePickerBtnProps = {
  setOpen: (value: boolean) => void
  open: boolean
  value: Date
}

type ValueProps = {
  date: Date | null
  startTime: Date | null
  endTime: Date | null
  orderQuota: string
  graceHours: string
  feeProducts: string
  deliveryFee: string
  visibilityStart: Date | null
  visibilityEnd: Date | null
  active: boolean
  recurrent: boolean
  validityStart: Date | null
  validityEnd: Date | null
  attributes?: boolean
}

type SlotContentProps = {
  deliverySlot?: boolean
  children?: ReactNode
}

const CalendarSelectInput = ({
  value,
  date,
  open,
  setOpen,
  inputProps,
  disabled,
  onChange,
  id,
  label,
  width,
}: CalendarSelectInputProps) => {
  return (
    <>
      <InputLabel
        htmlFor={id}
        sx={{ transform: 'translate(8px, 0px)', top: 0 }}>
        {label}
      </InputLabel>
      <input
        style={{ display: 'none' }}
        value={date}
        onChange={onChange}
        disabled={disabled}
        {...inputProps}
      />
      <DatePickerButton
        variant='outlined'
        onClick={() => setOpen(!open)}
        width={width}>
        {value ? formatDate(value as Date) : null}
      </DatePickerButton>
    </>
  )
}

const TimePickerBtn = ({ setOpen, open, value }: TimePickerBtnProps) => {
  return (
    <DatePickerButton
      variant='outlined'
      onClick={() => setOpen(!open)}
      width='100px'>
      {format(value, 'HH:mm')}
    </DatePickerButton>
  )
}

export const SlotContent = ({ deliverySlot, children }: SlotContentProps) => {
  const { t } = useTranslation()
  const { values, errors, setFieldValue }: FormikContextType<ValueProps> =
    useFormikContext()
  const { feeProducts } = useServiceAreas()
  const feeProductOptions =
    feeProducts &&
    feeProducts.map((product) => {
      return { label: product.name, value: product.masterProductId }
    })
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  return (
    <Form>
      <Box mb={4} display='flex' flexDirection='column'>
        <Box display='flex' justifyContent='space-between'>
          <Calendar
            ariaLabel={t('serviceArea.ariaSelectSlotDate')}
            id='date'
            inputWidth={isMobile ? '120px' : '216px'}
            label={<RequiredLabel inputLabel='serviceArea.date' />}
            disablePast
            value={values.date as Date}
            handleChange={(value) => {
              setFieldValue('date', value)
            }}
            selectBtn={CalendarSelectInput}
          />
          <TimePicker
            inputLabel={<RequiredLabel inputLabel='from' shortForm />}
            datePickerButton={TimePickerBtn}
            ariaLabel={t('serviceArea.startTimeAria')}
            value={values.startTime as Date}
            handleChange={(value) => {
              setFieldValue('startTime', value)
            }}
          />
          <TimePicker
            inputLabel={<RequiredLabel inputLabel='until' shortForm />}
            datePickerButton={TimePickerBtn}
            ariaLabel={t('serviceArea.endTimeAria')}
            value={values.endTime as Date}
            handleChange={(value) => {
              setFieldValue('endTime', value)
            }}
          />
        </Box>
        <Divider light sx={{ margin: `${theme.spacing(2)} 0` }} />
        <Typography variant='body2' mb={3}>
          {t('info')}
        </Typography>
        <Box display='flex' justifyContent='space-between'>
          <Input
            errorMessage={errors.orderQuota}
            label={t('serviceArea.orderQuota')}
            name='orderQuota'
            type='text'
            variant='filled'
            fullWidth
            width={isMobile ? '160px' : '48%'}
          />
          <Input
            errorMessage={errors.graceHours}
            label={t('serviceArea.graceHours')}
            name='graceHours'
            type='text'
            variant='filled'
            fullWidth
            width={isMobile ? '160px' : '48%'}
          />
        </Box>
        {feeProductOptions && feeProductOptions.length > 0 ? (
          <Field
            type='select'
            component={SelectDropdown}
            name='feeProducts'
            selectedValue={values.feeProducts}
            selectOptions={feeProductOptions}
            handleChange={(value: string) => {
              setFieldValue('feeProducts', value)
            }}
            label={<RequiredLabel inputLabel='serviceArea.feeProductInput' />}
            style={{ width: '100%' }}
          />
        ) : (
          <Input
            errorMessage={errors.deliveryFee}
            label={
              <RequiredLabel
                inputLabel='serviceArea.serviceFee'
                message='serviceArea.feeProductOrServiceFee'
              />
            }
            name='deliveryFee'
            type='text'
            variant='filled'
            fullWidth
            width='100%'
          />
        )}
        <Box display='flex' justifyContent='space-between' mb={3}>
          <Calendar
            ariaLabel={t('serviceArea.ariaSelectVisStart')}
            inputWidth={isMobile ? '160px' : '216px'}
            id='visibilityStart'
            label={
              <Typography variant='body2'>
                {t('serviceArea.visibilityStart')}
              </Typography>
            }
            value={values.visibilityStart}
            handleChange={(value) => {
              setFieldValue('visibilityStart', value)
            }}
            selectBtn={CalendarSelectInput}
          />
          <Calendar
            ariaLabel={t('serviceArea.ariaSelectVisEnd')}
            inputWidth={isMobile ? '160px' : '216px'}
            id='visibilityEnd'
            label={
              <Typography variant='body2'>
                {t('serviceArea.visibilityEnd')}
              </Typography>
            }
            value={values.visibilityEnd}
            handleChange={(value) => {
              setFieldValue('visibilityEnd', value)
            }}
            selectBtn={CalendarSelectInput}
          />
        </Box>
        <Stack spacing={2}>
          {deliverySlot && (
            <FormControlLabel
              sx={{ marginLeft: 0 }}
              control={
                <BlueSwitch
                  name='attributes'
                  value={values.attributes}
                  checked={values.attributes}
                  onChange={() => {
                    setFieldValue('attributes', !values.attributes)
                  }}
                />
              }
              label={
                <Typography variant='body2' ml={2}>
                  {t('instantDelivery')}
                </Typography>
              }
            />
          )}
          <FormControlLabel
            sx={{ marginLeft: 0 }}
            control={
              <BlueSwitch
                name='active'
                value={values.active}
                checked={values.active}
                onChange={() => {
                  setFieldValue('active', !values.active)
                }}
              />
            }
            label={
              <Typography variant='body2' ml={2}>
                {t('serviceArea.active')}
              </Typography>
            }
          />
        </Stack>
        <Divider light sx={{ margin: `${theme.spacing(2)} 0` }} />
        <CheckboxLabel
          value={values.recurrent}
          handleChange={() => setFieldValue('recurrent', !values.recurrent)}
          label={t('serviceArea.recurrentCheckboxLabel', {
            dayOfWeek: format(values.date as Date, 'EEEE'),
          })}
        />
        {values.recurrent && (
          <Box display='flex' justifyContent='space-between' mt={2} mb={4}>
            <Calendar
              ariaLabel={t('serviceArea.ariaSelectValidityStart')}
              id='validityStart'
              disablePast
              label={<RequiredLabel inputLabel='from' />}
              value={values.validityStart}
              handleChange={(value) => {
                setFieldValue('validityStart', value)
              }}
              selectBtn={CalendarSelectInput}
            />
            <Calendar
              ariaLabel={t('serviceArea.ariaSelectValidityEnd')}
              id='validityEnd'
              label={<RequiredLabel inputLabel='until' />}
              value={values.validityEnd}
              handleChange={(value) => {
                setFieldValue('validityEnd', value)
              }}
              selectBtn={CalendarSelectInput}
            />
          </Box>
        )}
      </Box>
      {children}
    </Form>
  )
}
